import {
    DataFlowDatasetSettings,
    DatasetFormatType
} from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { IViolation, Metamodel, stringIsNullOrEmpty } from 'shell-models-lib';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { DataFlowExpression } from '../dataflowexpression';
import { StringDataFlowType } from '../dataflow-datatype';
import { WorkdayLinkedService } from 'app/model/linkedservice/workday.linkedservice';
import { format, stringIsNullOrWhitespace } from 'app/shared/utils/string.util';
import HR from '!raw-loader!assets/xml/workday/Human_resources_v39_1.wsdl';
import XmlBeautify from '../../dataflow/xml-util';

interface IError {
    message: string,
    line: number,
    column: number
}

interface ISchemaNode {
    tagName: string,
    attributes: string[],
    minOccur: string,
    maxOccur: string,
    type: string,
    childNodes: ISchemaNode[]
}

@Metamodel.model({
    name: adfModelTypeMap.workday.dataflowSettings
})
export class WorkdaySettings extends DataFlowDatasetSettings {

    private WSDL: XMLDocument;

    private static validateRequestPayload(owner: WorkdaySettings, value: DataFlowExpression): IViolation[] {
        const violations: IViolation[] = [];
        if(value.templateExpression){
            return violations;
        }

        if(stringIsNullOrWhitespace(value.expression)){
            violations.push({
                context: owner,
                message: () => format(stringResources.RequiredTemplate, stringResources.Workday_Request_DisplayText)
            });
            return violations;
        }
        const parser = new DOMParser();
        const doc: XMLDocument = parser.parseFromString(value.expression, 'text/xml');
        if(doc.querySelector('parsererror')){
            const errors = doc.querySelector('parsererror').querySelector('div').textContent.split('\n').filter((err) => !stringIsNullOrWhitespace(err));
            violations.push({
                context: owner,
                message: () => format(stringResources.Error_Invalid_Request_Payload,  errors.length.toString(), errors.length>1?'s':'', errors.join('\n'))
            });
            return violations;
        }
        //Request payload is syntactically valid
        const invalidElementRegex = /<[^>]*>\s*\?\s*<\/[^<]*>/g;
        const invalidAttrRegex = /([^\s]*)(\s*)=(\s*)"(\s*)\?(\s*)"/g;
        const invalidElements = value.expression.match(invalidElementRegex);
        const invalidAttributes = value.expression.match(invalidAttrRegex);

        if(!invalidElements && !invalidAttributes)
            {return violations;}

        const errors: IError[] = [];
        const payloadLines = value.expression.split('\n');

        if(invalidElements){
            let payloadLine = 0;
            let errorCount = 0;
            const errorChecker = payloadLines.map((line) => ({line, start: 0}));
            while(errorCount < invalidElements.length){
                const pos = errorChecker[payloadLine].line.indexOf(invalidElements[errorCount], errorChecker[payloadLine].start);
                if(pos!==-1){
                    const errorPos = pos + invalidElements[errorCount].lastIndexOf('?') + 1;
                    errors.push({
                        message: format(stringResources.Error_Invalid_XML_Element, (payloadLine+1).toString(), errorPos.toString(), invalidElements[errorCount].match(/<[^\s>\/]*/)[0].substring(1)),
                        line: payloadLine+1,
                        column: errorPos
                    });
                    errorChecker[payloadLine].start = pos + invalidElements[errorCount++].length;
                }else{
                    payloadLine++;
                }
            }
        }

        if(invalidAttributes){
            let payloadLine = 0;
            let errorCount = 0;
            const errorChecker = payloadLines.map((line) => ({line, start: 0}));
            while(errorCount < invalidAttributes.length){
                const pos = errorChecker[payloadLine].line.indexOf(invalidAttributes[errorCount], errorChecker[payloadLine].start);
                if(pos!==-1){
                    const errorPos = pos + invalidAttributes[errorCount].lastIndexOf('?') + 1;
                    errors.push({
                        message: format(stringResources.Error_Invalid_XML_Attribute, (payloadLine+1).toString(), errorPos.toString(), invalidAttributes[errorCount].match(/[^\s=]*/)[0].substring(1)),
                        line: payloadLine+1,
                        column: errorPos
                    });
                    errorChecker[payloadLine].start = pos + invalidAttributes[errorCount++].length;
                }else{
                    payloadLine++;
                }
            }
        }

        function sortByPosition(a: IError, b: IError) {
            return a.line < b.line || (a.line === b.line && a.column < b.column) ? -1 : 1;
        }

        violations.push({
            context: owner,
            message: () => format(stringResources.Error_Invalid_Request_Payload,
                errors.length.toString(), errors.length>1?'s':'' ,
                errors.sort(sortByPosition).map((error: IError) => error.message).join('\n'))
        });

        return violations;
    }

    public getDefaultRequest(workdayLS: WorkdayLinkedService): string {
        if(!workdayLS){
            return '';
        }
        return this.getSampleRequestPayload(workdayLS.operation);
    }

    @Metamodel.attribute({
        constraint: {
            validator: WorkdaySettings.validateRequestPayload
        }
    })
    public request: DataFlowExpression;

    public getDatasetType(): string {
        // for now, Workday only support inline mode, it does not have dataset
        return undefined;
    }

    public getFormat(): DatasetFormatType {
        return DatasetFormatType.Workday;
    }

    public getIconClass(): string {
        return 'i-100x100-workday';
    }

    public getSubFormat(): string {
        return undefined;
    }

    public initialize(subFormat: string): void {
        //parse WSDL to XML Doc
        this.WSDL = new DOMParser().parseFromString(HR, 'text/xml');
        this.request = this.transform.createTemplatableExpression(StringDataFlowType.TYPE, '');
    }

    private getSampleRequestPayload(operation: string): string {

        const namespace = this.findNamespace();
        const namespaceName = namespace.substring(namespace.lastIndexOf('/')+1);
        const requestHeader = this.extractRequestHeader(operation, namespaceName);
        const requestBody = this.extractRequestBody(operation, namespaceName);

        const payload = format(stringResources.Workday_Request_XML_Wrapper,
            format(stringResources.Workday_Request_Namespace_Def, namespaceName, namespace),
            requestHeader, requestBody);
        return XmlBeautify.beautify(payload);
    }

    private findNamespace(): string {
        try{
            return this.WSDL.querySelector('schema').attributes.getNamedItem('targetNamespace').value;
        }catch{
            return '';
        }
    }

    private extractRequestHeader(operationName: string, namespace: string): string {
        // It has input definition and which header to use
        const operationBinding = this.getOperationBinding(operationName);
        // Get header message
        const headerMsgName = this.getHeaderMsgName(operationBinding);
        const headerMessage = this.getMessage(headerMsgName);
        // Get root header element
        const rootElementName = this.findRootElementName(headerMessage, 'header');
        const rootElement = this.getElement(rootElementName);
        return this.extractSampleSchema(rootElement, namespace);
    }

    private extractRequestBody(operationName: string, namespace: string): string {
        //get operation definition
        const operationDef = this.getOperationDef(operationName);
        // find body message
        const bodyMsgName = this.getBodyMsgName(operationDef);
        const bodyMessage =  this.getMessage(bodyMsgName);
        const rootElementName = this.findRootElementName(bodyMessage, 'body');
        const rootElement = this.getElement(rootElementName);
        return this.extractSampleSchema(rootElement, namespace);
    }

    private extractSampleSchema(rootElement: Element, namespace: string): string {
        // Construct structure tree
        const structTree = this.generateSchemaTree(rootElement);
        // return schema definition derived from struct tree
        return this.generateSampleRequest(structTree, namespace);
    }

    private generateSchemaTree(element: Element): ISchemaNode {
        if(!element){
            return null;
        }

        const schemaNode: ISchemaNode = {
            tagName: '',
            attributes: [],
            minOccur: '',
            maxOccur: '',
            type: '',
            childNodes: []
        };
        if(element.localName==='element'){
            schemaNode.type = 'element';
            schemaNode.tagName = element.attributes.getNamedItem('name').value;
            schemaNode.minOccur = this.getAttributeValue('minOccurs', element);
            schemaNode.maxOccur = this.getAttributeValue('maxOccurs', element);

            const tagType = this.getTagType(element);
            const complexTypes = this.WSDL.querySelector('types').querySelectorAll('complexType');
            const complexType = this.getDesiredComplexType(tagType, complexTypes);
            if(!complexType){
                return schemaNode;
            }

            schemaNode.attributes = this.getAttributes(complexType);
            const children = this.getComplexTypeChildren(complexType);
            schemaNode.childNodes = this.handleChildren(children);
        }else{
            schemaNode.type = 'choice';
            const children = this.getChoices(element);
            schemaNode.childNodes = this.handleChildren(children);
        }
        return schemaNode;
    }

    private getComplexTypeChildren(element: Element): Element[]{
        if(element.querySelector('sequence')){
            return Array.from(element.querySelector('sequence').childNodes as NodeListOf<Element>);
        }else if(element.querySelector('simpleContent')){
            return [];
        }
        return null;
    }

    private handleChildren(children: Element[]): ISchemaNode[] {
        if(!children){
            return null;
        }
        const schemaNodes: ISchemaNode[] = [];
        children.forEach((child) => {
            if(child.localName==='element' || child.localName==='choice'){
                schemaNodes.push(this.generateSchemaTree(child));
            }
        });
        return schemaNodes;
    }

    private getDesiredComplexType(target: string, list: NodeListOf<Element>): Element {
        if(!target || target==='simple'){
            return null;
        }
        for(let cnt = 0; cnt < list.length; cnt++){
            const element = list.item(cnt);
            if(element.attributes.getNamedItem('name').value===target){
                return element;
            }
        }
        return null;
    }

    private getElement(elementName: string): Element {
        if(!elementName){
            return null;
        }
        const elements = this.WSDL.querySelectorAll('element');
        for(let cnt = 0; cnt < elements.length; cnt++){
            const element = elements.item(cnt);
            if(element.attributes.getNamedItem('name').value===elementName){
                return element;
            }
        }
        return null;
    }

    private getChoices(element: Element): Element[] {
        return Array.from(element.childNodes as NodeListOf<Element>);
    }

    private getTagType(element: Element): string {
        if(!element.attributes.getNamedItem('type')){
            return 'simple';
        }
        const tagType = element.attributes.getNamedItem('type').value;
        return this.trimNS(tagType);
    }

    private getAttributeName(attributeTag: Element): string {
        let attributeName = '';
        attributeName = attributeTag.attributes.getNamedItem('name') ? attributeTag.attributes.getNamedItem('name').value : attributeTag.attributes.getNamedItem('ref').value;
        attributeName = this.trimNS(attributeName);
        // We have dedicated source option to specify 'version', so excluding from here
        if(attributeName==='version'){
            return null;
        }
        return attributeName;
    }

    private getAttributes(complexType: Element): string[] {
        const attributes: string[] = [];
        if(!complexType){
            return attributes;
        }
        const attributeTags = complexType.querySelectorAll('attribute');
        for(let cnt = 0; cnt < attributeTags.length; cnt++){
            const attributeName = this.getAttributeName(attributeTags.item(cnt));
            if(!stringIsNullOrEmpty(attributeName)){
                attributes.push(attributeName);
            }
        }
        return attributes;
    }

    private getAttributeValue(attribute: string, element: Element): string {
        if(element.attributes.getNamedItem(attribute)){
            return element.attributes.getNamedItem(attribute).value;
        }
        return '';
    }

    private findRootElementName(message: Element, type: string): string {
        const parts = message.querySelectorAll('part');
        for(let cnt=0; cnt<parts.length; cnt++){
            const part = parts.item(cnt);
            if(part.attributes.getNamedItem('name').value===type){
                const rootEleName = part.attributes.getNamedItem('element').value;
                return this.trimNS(rootEleName);
            }
        }
        return null;
    }

    private getMessage(messageName: string): Element {
        const messages = this.WSDL.querySelectorAll('message');
        for(let cnt = 0; cnt < messages.length; cnt++){
            const message = messages.item(cnt);
            if(message.attributes.getNamedItem('name').value===messageName){
                return message;
            }
        }
        return null;
    }

    private getHeaderMsgName(binding: Element): string {
        const headerMsgName = binding.querySelector('input').querySelector('header').attributes.getNamedItem('message').value;
        return this.trimNS(headerMsgName);
    }

    private getBodyMsgName(operationDef: Element): string {
        const bodyMsgName = operationDef.querySelector('input').attributes.getNamedItem('message').value;
        return this.trimNS(bodyMsgName);
    }

    private getOperationDef(selection: string): Element {
        const operations = this.WSDL.querySelector('portType').querySelectorAll('operation');
        for(let cnt = 0; cnt < operations.length; cnt++){
            const operation = operations.item(cnt);
            if(operation.attributes.getNamedItem('name').value===selection){
                return operation;
            }
        }
        return null;
    }

    private getOperationBinding(selection: string): Element {
        const operations = this.WSDL.querySelector('binding').querySelectorAll('operation');
        for(let cnt = 0; cnt < operations.length; cnt++){
            const operation = operations.item(cnt);
            if(operation.parentElement.localName==='binding' && operation.attributes.getNamedItem('name').value===selection){
                return operation;
            }
        }
        return null;
    }

    private generateSampleRequest(schemaTree: ISchemaNode, namespace: string): string {
        let sampleRequest = '';
        if(!schemaTree){
            return sampleRequest;
        }

        if(schemaTree.type==='choice'){
            sampleRequest += this.putMultiChoiceComment(schemaTree.childNodes.length);
            schemaTree.childNodes.forEach((child) => {
                sampleRequest += this.generateSampleRequest(child, namespace);
            });
            return sampleRequest;
        }

        sampleRequest += this.getElementCardinality(schemaTree.minOccur, schemaTree.maxOccur);
        sampleRequest += `<${namespace}:${schemaTree.tagName}`;
        schemaTree.attributes.forEach((attribute) => sampleRequest += ` ${namespace}:${attribute}="?"`);
        if(!schemaTree.childNodes){
            sampleRequest += '/>';
            return sampleRequest;
        }

        sampleRequest += '>';
        if(!schemaTree.childNodes.length){
            sampleRequest += '?';
        }else{
            schemaTree.childNodes.forEach((child) => {
                sampleRequest += this.generateSampleRequest(child, namespace);
            });
        }
        sampleRequest+=`</${namespace}:${schemaTree.tagName}>`;

        return sampleRequest;
    }

    private putMultiChoiceComment(choices: number): string{
        if(choices<2){
            return '';
        }
        return format(stringResources.Workday_Request_Choice, choices.toString());
    }

    private getElementCardinality(minOccur: string, maxOccur: string): string{
        switch (`${minOccur}/${maxOccur}`) {
            case '0/':
                return stringResources.Workday_Request_Cardinality_Optional;
            case '/unbounded':
                return stringResources.Workday_Request_Cardinality_1_Or_More;
            case '0/unbounded':
                return stringResources.Workday_Request_Cardinality_0_Or_More;
            case '0/1':
                return stringResources.Workday_Request_Cardinality_Optional;
            case '/':
                return '';
            case '/1':
                return '';
            default:
                return format(stringResources.Workday_Request_Cardinality_By_Occurs, minOccur, maxOccur);
        }
    }

    private trimNS(str: string): string {
        return str.substring(str.lastIndexOf(':')+1);
    }

}
